import { useState, useEffect, useCallback, useRef } from 'react';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';
import { TARGETED_REACTION_TIMEOUT } from '../components/ReactionsButtonGroup/ParticipantTargetedReaction';

/**
 * Custom hook to manage and synchronize participant reactions in a meeting.
 *
 * This hook provides functions to toggle the "raise hand" status and send temporary reactions,
 * while also keeping track of all participant reactions in the room.
 *
 * @returns {Object} An object containing reaction state and functions to update it
 */
const useReactions = () => {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const [participantReactions, setParticipantReactions] = useState({});
  const [localReactions, setLocalReactions] = useState({});
  const timeoutIdRef = useRef(null);
  useEffect(() => {
    if (!room) {
      return;
    }

    const handleParticipantMetadataChanged = (prevMetadata, participant) => {
      try {
        const metadata = participant.metadata
          ? JSON.parse(participant.metadata)
          : {};

        const prevData = prevMetadata ? JSON.parse(prevMetadata) : {};

        if (metadata.reactions !== prevData.reactions) {
          setParticipantReactions((current) => ({
            ...current,
            [participant.identity]: metadata.reactions || {},
          }));
        }
      } catch (error) {
        // eslint-disable-next-line
        console.error('Error parsing participant metadata:', error);
      }
    };

    room.remoteParticipants?.forEach((participant) => {
      if (participant.metadata) {
        try {
          const metadata = JSON.parse(participant.metadata);
          if (metadata.reactions) {
            setParticipantReactions((current) => ({
              ...current,
              [participant.identity]: metadata.reactions,
            }));
          }
        } catch (error) {
          // eslint-disable-next-line
          console.error('Error parsing participant metadata:', error);
        }
      }
    });

    if (localParticipant?.metadata) {
      try {
        const metadata = JSON.parse(localParticipant.metadata);
        if (metadata.reactions) {
          const reactions = metadata.reactions;
          setLocalReactions(reactions);
          setParticipantReactions((current) => ({
            ...current,
            [localParticipant.identity]: reactions,
          }));
        }
      } catch (error) {
        // eslint-disable-next-line
        console.error('Error parsing local participant metadata:', error);
      }
    }

    room.on('participantMetadataChanged', handleParticipantMetadataChanged);

    return () => {
      room.off('participantMetadataChanged', handleParticipantMetadataChanged);
    };
  }, [room, localParticipant]);

  const updateReactionMetadata = useCallback(
    (reactionData) => {
      if (!localParticipant) return;

      try {
        const currentMetadata = localParticipant.metadata
          ? JSON.parse(localParticipant.metadata)
          : {};

        const newMetadata = {
          ...currentMetadata,
          reactions: {
            ...currentMetadata.reactions,
            ...reactionData,
          },
        };

        localParticipant.setMetadata(JSON.stringify(newMetadata));
        setLocalReactions(newMetadata.reactions);

        setParticipantReactions((current) => ({
          ...current,
          [localParticipant.identity]: newMetadata.reactions,
        }));
      } catch (error) {
        // eslint-disable-next-line
        console.error('Error updating reaction metadata:', error);
      }
    },
    [localParticipant]
  );

  const toggleRaiseHand = useCallback(() => {
    if (!localParticipant) {
      return;
    }

    try {
      const isRaised = localReactions.raisedHand || false;

      updateReactionMetadata({
        raisedHand: !isRaised,
        raisedHandTimestamp: !isRaised ? Date.now() : null,
      });
    } catch (error) {
      // eslint-disable-next-line
      console.error('Error toggling raise hand:', error);
    }
  }, [localParticipant, localReactions, updateReactionMetadata]);

  const sendReaction = useCallback(
    (reactionType) => {
      if (!localParticipant) {
        return;
      }

      // Clear any existing timeout to prevent premature hiding
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      // Update the reaction metadata
      updateReactionMetadata({
        temporaryReaction: reactionType,
        temporaryReactionTimestamp: Date.now(),
      });

      // Set a new timeout to clear the reaction after 10 seconds
      timeoutIdRef.current = setTimeout(() => {
        updateReactionMetadata({
          temporaryReaction: null,
          temporaryReactionTimestamp: null,
        });
        timeoutIdRef.current = null; // Reset the timeout ID
      }, 10000);
    },
    [localParticipant, updateReactionMetadata]
  );

  const addTargetedReaction = useCallback(
    (targetIdentity, reactionEmoji) => {
      if (!localParticipant || !targetIdentity) return;

      updateReactionMetadata({
        targetReaction: reactionEmoji,
        targetReactionTimestamp: Date.now(),
        targetReactionRecipient: targetIdentity,
      });
    },
    [localParticipant, updateReactionMetadata]
  );

  const allReactions = {
    ...participantReactions,
  };

  return {
    participantReactions: allReactions,
    toggleRaiseHand,
    sendReaction,
    addTargetedReaction,
    getParticipantReaction: (identity) => allReactions[identity] || {},
    getTargetedReaction: (identity) => {
      const currentTime = Date.now();

      const activeReactions = Object.entries(allReactions)
        .filter(([, reactions]) => {
          const {
            targetReactionRecipient,
            targetReaction,
            targetReactionTimestamp,
          } = reactions || {};

          return (
            targetReactionRecipient === identity &&
            targetReaction &&
            targetReactionTimestamp &&
            currentTime - targetReactionTimestamp < TARGETED_REACTION_TIMEOUT
          );
        })
        .map(([senderIdentity, reactions]) => ({
          reaction: reactions.targetReaction,
          timestamp: reactions.targetReactionTimestamp,
          sender: senderIdentity,
        }));

      if (activeReactions.length === 0) return null;

      return activeReactions.reduce((latest, current) =>
        current.timestamp > latest.timestamp ? current : latest
      ).reaction;
    },
    hasRaisedHand: (identity) => allReactions[identity]?.raisedHand || false,
    getTemporaryReaction: (identity) =>
      allReactions[identity]?.temporaryReaction || null,
  };
};

export default useReactions;
